import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { listBousalaAttempts } from "@/lib/bousala/admin.functions";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { ClipboardList, FileText, Users, RefreshCw, ExternalLink, ChevronRight, ChevronLeft } from "lucide-react";

const PAGE_SIZE = 50;

type AttemptStatusFilter = "all" | "in_progress" | "completed" | "scored" | "report_ready";
type AttemptTierFilter = "all" | "free" | "full" | "premium";
type AttemptLangFilter = "all" | "ar" | "en";

const STATUS_FILTERS = new Set(["all", "in_progress", "completed", "scored", "report_ready"]);
const TIER_FILTERS = new Set(["all", "free", "full", "premium"]);
const LANG_FILTERS = new Set(["all", "ar", "en"]);

function parseFilters(search: Record<string, unknown>) {
  const status = typeof search.status === "string" && STATUS_FILTERS.has(search.status) ? search.status : "all";
  const tier = typeof search.tier === "string" && TIER_FILTERS.has(search.tier) ? search.tier : "all";
  const language = typeof search.language === "string" && LANG_FILTERS.has(search.language) ? search.language : "all";
  const q = typeof search.q === "string" ? search.q.slice(0, 120) : "";
  const hideTest = search.hideTest === true || search.hideTest === "true";
  const pageRaw = Number(search.page);
  const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;
  return {
    status: status as AttemptStatusFilter,
    tier: tier as AttemptTierFilter,
    language: language as AttemptLangFilter,
    q,
    hideTest,
    page,
  };
}

const attemptsQ = (filters: ReturnType<typeof parseFilters>) =>
  queryOptions({
    queryKey: ["admin", "bousala", "attempts", filters],
    queryFn: () =>
      listBousalaAttempts({
        data: {
          status: filters.status,
          tier: filters.tier,
          language: filters.language,
          search: filters.q,
          includeTest: !filters.hideTest,
          page: filters.page,
          pageSize: PAGE_SIZE,
        },
      }),
  });

export const Route = createFileRoute("/_authenticated/admin/bousala/attempts/")({
  validateSearch: parseFilters,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => context.queryClient.ensureQueryData(attemptsQ(deps)),
  head: () => ({ meta: [{ title: "محاولات بوصلة د. سارة" }, { name: "robots", content: "noindex" }] }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: AttemptsPage,
});

const STATUS_AR: Record<string, string> = {
  in_progress: "قيد الإجراء",
  completed: "مكتمل",
  scored: "مصحّح",
  report_ready: "التقرير جاهز",
};
const TIER_AR: Record<string, string> = { free: "مجاني", full: "كامل", premium: "بريميوم" };

function fmtDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("ar-u-nu-latn", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "report_ready"
      ? "bg-soft-green/30 text-foreground"
      : status === "scored"
      ? "bg-soft-orange/30 text-foreground"
      : status === "completed"
      ? "bg-primary/15 text-foreground"
      : "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider ${cls}`}>{STATUS_AR[status] ?? status}</span>;
}

function TierBadge({ tier }: { tier: string }) {
  const cls = tier === "premium" ? "bg-primary/20 text-foreground" : tier === "full" ? "bg-soft-green/30 text-foreground" : "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider ${cls}`}>{TIER_AR[tier] ?? tier}</span>;
}

function LangBadge({ language }: { language: "ar" | "en" }) {
  const cls = language === "en" ? "bg-soft-blue/30 text-foreground" : "bg-muted text-muted-foreground";
  const label = language === "en" ? "EN" : "AR";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wider ${cls}`}>{label}</span>;
}

function AttemptsPage() {
  const filters = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data } = useSuspenseQuery(attemptsQ(filters));
  const qc = useQueryClient();
  const router = useRouter();
  const [refreshing, setRefreshing] = useState(false);
  const totalCount = data?.total ?? 0;
  const rows = data?.rows ?? [];
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(filters.page, pageCount);

  const filtersActive =
    filters.status !== "all" ||
    filters.tier !== "all" ||
    filters.language !== "all" ||
    filters.q.trim().length > 0 ||
    filters.hideTest;

  function setFilter(next: Partial<typeof filters>) {
    navigate({ to: ".", search: { ...filters, ...next, page: 1 } });
  }

  function goToPage(page: number) {
    navigate({ to: ".", search: { ...filters, page: Math.max(1, Math.min(page, pageCount)) } });
  }

  function resetFilters() {
    navigate({ to: ".", search: { status: "all", tier: "all", language: "all", q: "", hideTest: false, page: 1 } });
  }

  async function refresh() {
    setRefreshing(true);
    try {
      await qc.invalidateQueries({ queryKey: ["admin", "bousala", "attempts"] });
      await router.invalidate();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="space-y-6" dir="rtl">
      <header>
        <p className="kicker text-modern-orange">بوصلة د. سارة</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-extrabold">محاولات المستخدمين</h1>
          <Button type="button" size="sm" variant="secondary" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`ms-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            تحديث القائمة
          </Button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">كل محاولة تظهر هنا مع رابط واضح لحالتها وتقريرها داخل لوحة الإدارة.</p>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 py-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground">الحالة</label>
            <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value as AttemptStatusFilter })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
              <option value="all">الكل</option>
              <option value="in_progress">قيد الإجراء</option>
              <option value="completed">مكتمل</option>
              <option value="scored">مصحّح</option>
              <option value="report_ready">التقرير جاهز</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground">الفئة</label>
            <select value={filters.tier} onChange={(e) => setFilter({ tier: e.target.value as AttemptTierFilter })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
              <option value="all">الكل</option>
              <option value="free">مجاني</option>
              <option value="full">كامل</option>
              <option value="premium">بريميوم</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground">اللغة</label>
            <select value={filters.language} onChange={(e) => setFilter({ language: e.target.value as AttemptLangFilter })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
              <option value="all">الكل</option>
              <option value="ar">العربية</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="flex min-w-[220px] flex-1 flex-col gap-1">
            <label className="text-[10px] font-bold text-muted-foreground">بحث بالاسم أو المعرّف</label>
            <Input value={filters.q} onChange={(e) => setFilter({ q: e.target.value })} placeholder="اسم المستخدم أو attempt id" />
          </div>
          <label className="mt-5 flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={filters.hideTest} onChange={(e) => setFilter({ hideTest: e.target.checked })} className="h-4 w-4" />
            إخفاء بيانات الاختبار
          </label>
          {filtersActive && (
            <Button type="button" variant="secondary" size="sm" className="mt-5" onClick={resetFilters}>
              إعادة ضبط التصفية
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="rounded-2xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-soft">
        إجمالي المحاولات: <b className="text-foreground">{totalCount}</b> · صفحة <b className="text-foreground">{currentPage}</b> من{" "}
        <b className="text-foreground">{pageCount}</b> · الظاهر الآن: <b className="text-foreground">{rows.length}</b>
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <ClipboardList className="h-10 w-10 text-muted-foreground" />
            {totalCount > 0 && filters.page > 1 ? (
              <>
                <p className="text-sm text-muted-foreground">لا توجد نتائج في هذه الصفحة.</p>
                <Button type="button" variant="secondary" onClick={() => goToPage(1)}>الرجوع للصفحة الأولى</Button>
              </>
            ) : totalCount > 0 && filtersActive ? (
              <>
                <p className="text-sm text-muted-foreground">توجد محاولات محفوظة، لكن عوامل التصفية الحالية تخفيها.</p>
                <Button type="button" variant="secondary" onClick={resetFilters}>عرض كل المحاولات</Button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                لا توجد محاولات محفوظة حتى الآن. يمكنك
                <Link to="/bousala" className="mx-1 font-bold text-primary underline">بدء محاولة تجريبية</Link>
                ثم الرجوع هنا لمعاينتها.
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <ul className="grid grid-cols-1 gap-3">
          {rows.map((r) => (
            <li key={r.id}>
              <Card>
                <CardContent className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <p className="font-display text-base font-bold">{r.client_name?.trim() || "مستخدم بدون اسم"}</p>
                      <StatusBadge status={r.status} />
                      <TierBadge tier={r.tier} />
                      <LangBadge language={((r as any).language === "en" ? "en" : "ar") as "ar" | "en"} />
                      {r.is_test && <span className="rounded-full bg-soft-orange/40 px-2 py-0.5 text-[10px] font-bold">بيانات اختبار</span>}
                      {r.has_report && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold">تقرير محفوظ</span>}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground" dir="ltr">{r.id}</p>
                    <p className="mt-1 text-xs text-muted-foreground">بدأ: {fmtDate(r.started_at)} · اكتمل: {fmtDate(r.completed_at)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:justify-end">
                    <Button asChild size="sm">
                      <Link to="/admin/bousala/attempts/$attemptId" params={{ attemptId: r.id }}>
                        <FileText className="ms-1 h-4 w-4" />
                        {r.has_report || r.status === "report_ready" ? "عرض التقرير" : r.status === "scored" ? "توليد التقرير" : "فتح المحاولة"}
                      </Link>
                    </Button>
                    <Button asChild size="sm" variant="ghost">
                      <Link to="/portal/bousala/$attemptId" params={{ attemptId: r.id }} title="فتح كما يراها المستخدم">
                        <ExternalLink className="ms-1 h-4 w-4" />
                        عرض المستخدم
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {pageCount > 1 && (
        <nav aria-label="تنقل بين الصفحات" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-card px-4 py-3 shadow-soft">
          <Button
            type="button"
            variant="secondary"
            className="min-h-11"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
          >
            <ChevronRight className="ms-1 h-4 w-4" />
            السابق
          </Button>
          <span className="text-sm text-muted-foreground">
            صفحة <b className="text-foreground">{currentPage}</b> من <b className="text-foreground">{pageCount}</b>
          </span>
          <Button
            type="button"
            variant="secondary"
            className="min-h-11"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= pageCount}
          >
            التالي
            <ChevronLeft className="me-1 h-4 w-4" />
          </Button>
        </nav>
      )}
    </div>
  );
}